import React, { useEffect, useState } from "react";
import Template from "../../components/Template";
import api from "../../services/api";
import { formatCurrencyFromDb } from "../../utils/masks";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoEmpresa from "../../components/logoEmpresaBase64";
import { useAuth } from "../../context/AuthContext";

/**
 * Relatório Acompanhamento Financeiro de Obras.
 *
 * Filtro obrigatório: Cliente. As obras do cliente selecionado são listadas
 * com Orçamento Aprovado, Valor NF (soma de medições), Recebido (soma do
 * Valor Medido Líquido das medições), Material (soma das NFs de material —
 * canceladas excluídas), Serviço (NFs de serviço — canceladas excluídas) e
 * Saldo (Recebido - (Material + Serviço)).
 */
const AcompanhamentoFinanceiroObras = () => {
    const { auth } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [clienteId, setClienteId] = useState("");
    const [dados, setDados] = useState(null); // { cliente, linhas, totais }
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState("");

    useEffect(() => {
        const carregarClientes = async () => {
            try {
                const res = await api.get("/clientes/todos");
                setClientes(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                console.error("Erro ao carregar clientes", e);
                setClientes([]);
            }
        };
        carregarClientes();
    }, []);

    const gerarRelatorio = async () => {
        if (!clienteId) {
            alert("Selecione um cliente.");
            return;
        }
        setLoading(true);
        setErro("");
        setDados(null);
        try {
            const res = await api.get("/relatorios/acompanhamento-financeiro", {
                params: { clienteId },
            });
            setDados(res.data);
        } catch (e) {
            console.error(e);
            setErro(e.response?.data?.error || "Erro ao gerar relatório.");
        } finally {
            setLoading(false);
        }
    };

    const limparFiltros = () => {
        setClienteId("");
        setDados(null);
        setErro("");
    };

    // ============ EXPORT EXCEL (com cores nos headers) ============
    const exportarExcel = () => {
        if (!dados) return;
        const headerStyle = (fill) => ({
            font: { bold: true, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: fill } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
                top: { style: "thin", color: { rgb: "000000" } },
                bottom: { style: "thin", color: { rgb: "000000" } },
                left: { style: "thin", color: { rgb: "000000" } },
                right: { style: "thin", color: { rgb: "000000" } },
            },
        });
        const cellStyle = {
            alignment: { horizontal: "right", vertical: "center" },
            border: {
                top: { style: "thin", color: { rgb: "CCCCCC" } },
                bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                left: { style: "thin", color: { rgb: "CCCCCC" } },
                right: { style: "thin", color: { rgb: "CCCCCC" } },
            },
            numFmt: 'R$ #,##0.00',
        };
        const totalStyle = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "16A34A" } },
            alignment: { horizontal: "right", vertical: "center" },
            numFmt: 'R$ #,##0.00',
        };
        // Saldo negativo (célula de dado): fundo vermelho claro, texto vermelho escuro, bold
        const cellStyleNegativo = {
            font: { bold: true, color: { rgb: "991B1B" } },
            fill: { fgColor: { rgb: "FEE2E2" } },
            alignment: { horizontal: "right", vertical: "center" },
            border: {
                top: { style: "thin", color: { rgb: "CCCCCC" } },
                bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                left: { style: "thin", color: { rgb: "CCCCCC" } },
                right: { style: "thin", color: { rgb: "CCCCCC" } },
            },
            numFmt: 'R$ #,##0.00',
        };
        // Saldo negativo na linha de TOTAL GERAL: mantém negrito branco mas fundo vermelho forte
        const totalStyleNegativo = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "DC2626" } },
            alignment: { horizontal: "right", vertical: "center" },
            numFmt: 'R$ #,##0.00',
        };

        const ws = {};
        const tituloLinha = `UNIDADES — ${dados.cliente?.nome || ""}`;
        ws["A1"] = { v: tituloLinha, s: { font: { bold: true, sz: 14 }, alignment: { horizontal: "center" } } };

        // Linha 3: grupos de cabeçalho. Linha 4: títulos das colunas.
        // A3:A4 mescla "Unidade" verticalmente. B3:D3 = "Acompanhamento Econômico" (azul).
        // E3:G3 = "Acompanhamento Financeiro" (rosa).
        ws["A3"] = { v: "Unidade", s: headerStyle("FEF3C7") };
        ws["B3"] = { v: "Acompanhamento Econômico", s: headerStyle("BFDBFE") };
        ws["E3"] = { v: "Acompanhamento Financeiro", s: headerStyle("FBCFE8") };
        // Preencher células mescladas (necessário para o estilo aplicar visualmente em alguns leitores)
        ws["C3"] = { v: "", s: headerStyle("BFDBFE") };
        ws["D3"] = { v: "", s: headerStyle("BFDBFE") };
        ws["F3"] = { v: "", s: headerStyle("FBCFE8") };
        ws["G3"] = { v: "", s: headerStyle("FBCFE8") };
        ws["A4"] = { v: "", s: headerStyle("FEF3C7") };

        ws["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, // título A1:G1
            { s: { r: 2, c: 0 }, e: { r: 3, c: 0 } }, // Unidade A3:A4 (rowspan)
            { s: { r: 2, c: 1 }, e: { r: 2, c: 3 } }, // Acompanhamento Econômico B3:D3
            { s: { r: 2, c: 4 }, e: { r: 2, c: 6 } }, // Acompanhamento Financeiro E3:G3
        ];

        // Linha 4: títulos das colunas (azul para econômico, rosa para financeiro)
        const colHeaders = [
            { col: "B", v: "Orçamento Aprovado", fill: "DBEAFE" },
            { col: "C", v: "Valor NF", fill: "DBEAFE" },
            { col: "D", v: "Recebido", fill: "DBEAFE" },
            { col: "E", v: "Material", fill: "FCE7F3" },
            { col: "F", v: "Serviço", fill: "FCE7F3" },
            { col: "G", v: "Saldo", fill: "FCE7F3" },
        ];
        colHeaders.forEach((h) => {
            ws[`${h.col}4`] = { v: h.v, s: headerStyle(h.fill) };
        });

        // Linhas de dados (a partir da 5)
        let rowIdx = 5;
        dados.linhas.forEach((l) => {
            ws[`A${rowIdx}`] = { v: l.unidade || "", s: { alignment: { horizontal: "left", vertical: "center" } } };
            ws[`B${rowIdx}`] = { v: l.orcamentoAprovado || 0, t: "n", s: cellStyle };
            ws[`C${rowIdx}`] = { v: l.valorNF || 0, t: "n", s: cellStyle };
            ws[`D${rowIdx}`] = { v: l.recebido || 0, t: "n", s: cellStyle };
            ws[`E${rowIdx}`] = { v: l.material || 0, t: "n", s: cellStyle };
            ws[`F${rowIdx}`] = { v: l.servico || 0, t: "n", s: cellStyle };
            ws[`G${rowIdx}`] = { v: l.saldo || 0, t: "n", s: (Number(l.saldo) < 0 ? cellStyleNegativo : cellStyle) };
            rowIdx++;
        });

        // Total geral
        ws[`A${rowIdx}`] = { v: "TOTAL GERAL", s: { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "16A34A" } }, alignment: { horizontal: "left" } } };
        ws[`B${rowIdx}`] = { v: dados.totais.orcamentoAprovado || 0, t: "n", s: totalStyle };
        ws[`C${rowIdx}`] = { v: dados.totais.valorNF || 0, t: "n", s: totalStyle };
        ws[`D${rowIdx}`] = { v: dados.totais.recebido || 0, t: "n", s: totalStyle };
        ws[`E${rowIdx}`] = { v: dados.totais.material || 0, t: "n", s: totalStyle };
        ws[`F${rowIdx}`] = { v: dados.totais.servico || 0, t: "n", s: totalStyle };
        ws[`G${rowIdx}`] = { v: dados.totais.saldo || 0, t: "n", s: (Number(dados.totais.saldo) < 0 ? totalStyleNegativo : totalStyle) };

        ws["!ref"] = `A1:G${rowIdx}`;
        ws["!cols"] = [
            { wch: 40 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Acompanhamento");
        XLSX.writeFile(wb, `acompanhamento-financeiro-${(dados.cliente?.nome || "cliente").replace(/\s+/g, "_")}.xlsx`);
    };

    // ============ EXPORT PDF (replicando visual do print) ============
    const exportarPDF = () => {
        if (!dados) return;
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();

        // Logo
        try {
            doc.addImage(logoEmpresa, "PNG", 10, 8, 20, 16);
        } catch { /* ignore */ }

        // Título
        doc.setFontSize(13);
        doc.setFont(undefined, "bold");
        doc.text(`UNIDADES — ${dados.cliente?.nome || ""}`, pageWidth / 2, 16, { align: "center" });
        doc.setFontSize(9);
        doc.setFont(undefined, "normal");
        doc.text(`Emitido em ${new Date().toLocaleString("pt-BR")} por ${auth?.user?.name || ""}`, pageWidth / 2, 22, { align: "center" });

        // Cabeçalho de 2 linhas: grupos (Acompanhamento Econômico/Financeiro) + títulos das colunas
        const head = [
            [
                { content: "Unidade", rowSpan: 2, styles: { fillColor: [254, 243, 199], textColor: 0, halign: "center", valign: "middle" } },
                { content: "Acompanhamento Econômico", colSpan: 3, styles: { fillColor: [191, 219, 254], textColor: 0, halign: "center" } },
                { content: "Acompanhamento Financeiro", colSpan: 3, styles: { fillColor: [251, 207, 232], textColor: 0, halign: "center" } },
            ],
            [
                { content: "Orçamento Aprovado", styles: { fillColor: [219, 234, 254], textColor: 0, halign: "center" } },
                { content: "Valor NF", styles: { fillColor: [219, 234, 254], textColor: 0, halign: "center" } },
                { content: "Recebido", styles: { fillColor: [219, 234, 254], textColor: 0, halign: "center" } },
                { content: "Material", styles: { fillColor: [252, 231, 243], textColor: 0, halign: "center" } },
                { content: "Serviço", styles: { fillColor: [252, 231, 243], textColor: 0, halign: "center" } },
                { content: "Saldo", styles: { fillColor: [252, 231, 243], textColor: 0, halign: "center" } },
            ],
        ];

        const saldoCellStyle = (saldo) => (Number(saldo) < 0
            ? { fillColor: [254, 226, 226], textColor: [153, 27, 27], fontStyle: "bold" }
            : undefined);

        const body = dados.linhas.map((l) => [
            l.unidade || "",
            formatCurrencyFromDb(l.orcamentoAprovado),
            formatCurrencyFromDb(l.valorNF),
            formatCurrencyFromDb(l.recebido),
            formatCurrencyFromDb(l.material),
            formatCurrencyFromDb(l.servico),
            (Number(l.saldo) < 0
                ? { content: formatCurrencyFromDb(l.saldo), styles: saldoCellStyle(l.saldo) }
                : formatCurrencyFromDb(l.saldo)),
        ]);

        const foot = [[
            { content: "TOTAL GERAL", styles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold" } },
            { content: formatCurrencyFromDb(dados.totais.orcamentoAprovado), styles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold" } },
            { content: formatCurrencyFromDb(dados.totais.valorNF), styles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold" } },
            { content: formatCurrencyFromDb(dados.totais.recebido), styles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold" } },
            { content: formatCurrencyFromDb(dados.totais.material), styles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold" } },
            { content: formatCurrencyFromDb(dados.totais.servico), styles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold" } },
            { content: formatCurrencyFromDb(dados.totais.saldo), styles: (Number(dados.totais.saldo) < 0
                ? { fillColor: [220, 38, 38], textColor: [255, 255, 255], fontStyle: "bold" }
                : { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold" }) },
        ]];

        autoTable(doc, {
            startY: 28,
            head,
            body,
            foot,
            styles: { fontSize: 8, cellPadding: 2 },
            columnStyles: {
                0: { halign: "left", cellWidth: 70 },
                1: { halign: "right" },
                2: { halign: "right" },
                3: { halign: "right" },
                4: { halign: "right" },
                5: { halign: "right" },
                6: { halign: "right" },
            },
            theme: "grid",
        });

        doc.save(`acompanhamento-financeiro-${(dados.cliente?.nome || "cliente").replace(/\s+/g, "_")}.pdf`);
    };

    return (
        <Template>
            <div style={{ padding: "20px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: "700", marginBottom: "20px", color: "#1F2937" }}>
                    Acompanhamento Financeiro de Obras
                </h1>

                {/* Filtros */}
                <div style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "16px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                }}>
                    <div style={{ flex: "1 1 280px" }}>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", marginBottom: "6px", color: "#374151" }}>
                            Cliente *
                        </label>
                        <select
                            value={clienteId}
                            onChange={(e) => setClienteId(e.target.value)}
                            style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px" }}
                        >
                            <option value="">-- selecione --</option>
                            {clientes.map((c) => (
                                <option key={c.id} value={c.id}>{c.nome}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            type="button"
                            onClick={gerarRelatorio}
                            disabled={loading || !clienteId}
                            style={{
                                padding: "10px 18px",
                                backgroundColor: !clienteId ? "#93C5FD" : "#1E78B4",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: !clienteId ? "not-allowed" : "pointer",
                                fontWeight: "600",
                            }}
                        >
                            {loading ? "Gerando..." : "Gerar Relatório"}
                        </button>
                        <button
                            type="button"
                            onClick={limparFiltros}
                            style={{
                                padding: "10px 18px",
                                backgroundColor: "white",
                                color: "#374151",
                                border: "1px solid #D1D5DB",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "500",
                            }}
                        >
                            Limpar
                        </button>
                    </div>
                </div>

                {erro && (
                    <div style={{ backgroundColor: "#FEE2E2", border: "1px solid #FCA5A5", color: "#991B1B", padding: "12px 16px", borderRadius: "6px", marginBottom: "16px" }}>
                        {erro}
                    </div>
                )}

                {/* Resultado */}
                {dados && (
                    <div style={{ backgroundColor: "white", borderRadius: "8px", padding: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
                            <div>
                                <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1F2937" }}>
                                    UNIDADES — {dados.cliente?.nome}
                                </h2>
                                <div style={{ fontSize: "13px", color: "#6B7280" }}>
                                    {dados.linhas.length} obra{dados.linhas.length === 1 ? "" : "s"} encontrada{dados.linhas.length === 1 ? "" : "s"}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    type="button"
                                    onClick={exportarExcel}
                                    disabled={dados.linhas.length === 0}
                                    style={{
                                        padding: "8px 14px",
                                        backgroundColor: "#16A34A",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: dados.linhas.length === 0 ? "not-allowed" : "pointer",
                                        fontWeight: "600",
                                        opacity: dados.linhas.length === 0 ? 0.5 : 1,
                                    }}
                                >
                                    Exportar Excel
                                </button>
                                <button
                                    type="button"
                                    onClick={exportarPDF}
                                    disabled={dados.linhas.length === 0}
                                    style={{
                                        padding: "8px 14px",
                                        backgroundColor: "#DC2626",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: dados.linhas.length === 0 ? "not-allowed" : "pointer",
                                        fontWeight: "600",
                                        opacity: dados.linhas.length === 0 ? 0.5 : 1,
                                    }}
                                >
                                    Exportar PDF
                                </button>
                            </div>
                        </div>

                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr>
                                        <th rowSpan={2} style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#FEF3C7", textAlign: "left", fontWeight: "700" }}>Unidade</th>
                                        <th colSpan={3} style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#BFDBFE", textAlign: "center", fontWeight: "700" }}>Acompanhamento Econômico</th>
                                        <th colSpan={3} style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#FBCFE8", textAlign: "center", fontWeight: "700" }}>Acompanhamento Financeiro</th>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#DBEAFE", textAlign: "right", fontWeight: "700" }}>Orçamento Aprovado</th>
                                        <th style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#DBEAFE", textAlign: "right", fontWeight: "700" }}>Valor NF</th>
                                        <th style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#DBEAFE", textAlign: "right", fontWeight: "700" }}>Recebido</th>
                                        <th style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#FCE7F3", textAlign: "right", fontWeight: "700" }}>Material</th>
                                        <th style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#FCE7F3", textAlign: "right", fontWeight: "700" }}>Serviço</th>
                                        <th style={{ padding: "10px", border: "1px solid #E5E7EB", backgroundColor: "#FCE7F3", textAlign: "right", fontWeight: "700" }}>Saldo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dados.linhas.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ padding: "24px", textAlign: "center", color: "#9CA3AF" }}>
                                                Nenhuma obra encontrada para este cliente.
                                            </td>
                                        </tr>
                                    ) : (
                                        dados.linhas.map((l) => (
                                            <tr key={l.id}>
                                                <td style={{ padding: "8px 10px", border: "1px solid #E5E7EB" }}>{l.unidade}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #E5E7EB", textAlign: "right" }}>{formatCurrencyFromDb(l.orcamentoAprovado)}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #E5E7EB", textAlign: "right" }}>{formatCurrencyFromDb(l.valorNF)}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #E5E7EB", textAlign: "right" }}>{formatCurrencyFromDb(l.recebido)}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #E5E7EB", textAlign: "right" }}>{formatCurrencyFromDb(l.material)}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #E5E7EB", textAlign: "right" }}>{formatCurrencyFromDb(l.servico)}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #E5E7EB", textAlign: "right", fontWeight: "600", color: l.saldo < 0 ? "#DC2626" : "#059669" }}>{formatCurrencyFromDb(l.saldo)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {dados.linhas.length > 0 && (
                                    <tfoot>
                                        <tr style={{ backgroundColor: "#16A34A", color: "white" }}>
                                            <td style={{ padding: "10px", fontWeight: "700" }}>TOTAL GERAL</td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: "700" }}>{formatCurrencyFromDb(dados.totais.orcamentoAprovado)}</td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: "700" }}>{formatCurrencyFromDb(dados.totais.valorNF)}</td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: "700" }}>{formatCurrencyFromDb(dados.totais.recebido)}</td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: "700" }}>{formatCurrencyFromDb(dados.totais.material)}</td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: "700" }}>{formatCurrencyFromDb(dados.totais.servico)}</td>
                                            <td style={{ padding: "10px", textAlign: "right", fontWeight: "700" }}>{formatCurrencyFromDb(dados.totais.saldo)}</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Template>
    );
};

export default AcompanhamentoFinanceiroObras;
